import React from 'react';
import { User } from 'lucide-react';
import { cn } from '../../lib/utils';
import Tooltip, { TooltipPosition } from './Tooltip';
import { getFormattedAssigneeName } from '../../utils/assigneeHelper';
import { getCachedUser, getInitials } from '../../utils/userCache';
import { COLOR_MAPPINGS, getDeterministicColor } from '../../utils/colorUtils';
import { logger } from '../../utils/logger';

interface UserInfoTooltipProps {
  /** User ID to display information for */
  userId: string;
  /** Fallback name if user data not in cache */
  fallbackName?: string;
  /** Size of the user icon */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Position of the tooltip */
  position?: TooltipPosition;
  /** Additional classes for the trigger icon */
  className?: string;
  /** If true, tooltip stays open on click */
  clickToToggle?: boolean;
  /** Background color for the icon (defaults to a deterministic color based on user ID) */
  bgColor?: string;
  /** Type of icon to display */
  iconType?: 'circle' | 'badge' | 'avatar';
  /** When true, shows the avatar image if available */
  showAvatar?: boolean;
}

/**
 * A tooltip that displays user information on hover
 */
const UserInfoTooltip: React.FC<UserInfoTooltipProps> = ({
  userId,
  fallbackName,
  size = 'sm',
  position = 'auto',
  className,
  clickToToggle = false, // Not needed for hover-only tooltips
  bgColor,
  iconType = 'circle',
  showAvatar = true,
}) => {
  // Get the user data from cache
  const cachedUser = getCachedUser(userId);

  // Generate consistent background color for each user if not provided
  const getBackgroundColor = () => {
    if (bgColor) return bgColor;
    return getDeterministicColor(userId, 'bg');
  };

  // Define size classes
  const sizeClasses = {
    xs: {
      icon: 'w-5 h-5',
      iconSize: 14, // Increased from 10
      badgeTop: 'top-0',
      badgeRight: 'right-0',
      badge: 'w-3 h-3',
      avatarSize: 'w-5 h-5',
      tooltipAvatarSize: 'w-7 h-7',
      tooltipInitialsSize: 'text-xs',
    },
    sm: {
      icon: 'w-6 h-6',
      iconSize: 16, // Increased from 12
      badgeTop: 'top-0',
      badgeRight: 'right-0',
      badge: 'w-3.5 h-3.5',
      avatarSize: 'w-6 h-6',
      tooltipAvatarSize: 'w-8 h-8',
      tooltipInitialsSize: 'text-xs',
    },
    md: {
      icon: 'w-8 h-8',
      iconSize: 20, // Increased from 16
      badgeTop: 'top-0',
      badgeRight: 'right-0',
      badge: 'w-4 h-4',
      avatarSize: 'w-8 h-8',
      tooltipAvatarSize: 'w-10 h-10',
      tooltipInitialsSize: 'text-sm',
    },
    lg: {
      icon: 'w-10 h-10',
      iconSize: 24, // Increased from 20
      badgeTop: 'top-0',
      badgeRight: 'right-0',
      badge: 'w-5 h-5',
      avatarSize: 'w-10 h-10',
      tooltipAvatarSize: 'w-12 h-12',
      tooltipInitialsSize: 'text-base',
    },
  };

  // Define icon rendering based on type
  const renderIcon = () => {
    const colorClass = getBackgroundColor();
    // Extract the color name from bgColor (e.g. "bg-blue-100" -> "blue")
    let iconColor = 'text-gray-500';
    switch (iconType) {
      case 'badge':
        return (
          <div className="relative">
            <div
              className={cn(
                sizeClasses[size].badge,
                'absolute rounded-full',
                colorClass,
                sizeClasses[size].badgeTop,
                sizeClasses[size].badgeRight
              )}
            />
          </div>
        );

      case 'avatar':
        // Show user avatar if available, otherwise initials
        if (showAvatar && cachedUser?.avatar_url) {
          return (
            <img
              src={cachedUser.avatar_url}
              alt="User avatar"
              className={cn(
                sizeClasses[size].avatarSize,
                'rounded-full object-cover',
                'border border-gray-200'
              )}
              crossOrigin="anonymous"
            />
          );
        } else {
          const initials = getInitials(
            cachedUser?.display_name || fallbackName || userId.substring(0, 2)
          );

          return (
            <div
              className={cn(
                sizeClasses[size].avatarSize,
                'rounded-full flex items-center justify-center',
                colorClass,
                'border border-gray-200 text-gray-700 font-medium'
              )}
            >
              {initials}
            </div>
          );
        }

      case 'circle':
      default:
        if (bgColor) {
          const colorMatch = bgColor.match(/bg-([a-z]+)-/);
          if (colorMatch && colorMatch[1]) {
            const colorName = colorMatch[1];
            // Use the COLOR_MAPPINGS from colorUtils
            iconColor = COLOR_MAPPINGS.icon[colorName] || 'text-gray-500';
          }
        }

        return (
          <div
            className={cn(
              sizeClasses[size].icon,
              'flex items-center justify-center pr-1',
              'transition-all'
            )}
          >
            <User size={sizeClasses[size].iconSize} className={iconColor} />
          </div>
        );
    }
  };

  // Tooltip content with user information
  const tooltipContent = (
    <div className="w-44 p-2 block">
      <div className="flex items-center space-x-3">
        {/* User avatar or initials */}
        {cachedUser?.avatar_url && showAvatar ? (
          <img
            src={cachedUser.avatar_url}
            alt="User avatar"
            className={cn(
              sizeClasses[size].tooltipAvatarSize,
              'rounded-full object-cover flex-shrink-0 border border-gray-200'
            )}
            crossOrigin="anonymous"
          />
        ) : (
          <div
            className={cn(
              sizeClasses[size].tooltipAvatarSize,
              'rounded-full flex items-center justify-center flex-shrink-0',
              getBackgroundColor(),
              sizeClasses[size].tooltipInitialsSize,
              'font-medium text-gray-700'
            )}
          >
            {getInitials(cachedUser?.display_name || fallbackName || userId.substring(0, 2))}
          </div>
        )}

        {/* User info - only name, no email */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">
            {getFormattedAssigneeName(userId, {
              fallbackName,
              compact: false,
            })}
          </p>
        </div>
      </div>
    </div>
  );

  // Add debug logging
  const loggerAvailable = typeof logger !== 'undefined';
  const log = (message: string, data?: any) => {
    if (loggerAvailable) {
      try {
        // @ts-ignore - logger is imported dynamically
        logger.component(message, data);
      } catch {
        console.log(message, data);
      }
    } else {
      console.log(message, data);
    }
  };

  return (
    <Tooltip
      content={tooltipContent}
      position={position}
      clickToToggle={clickToToggle}
      triggerClassName={className}
      delay={0}
      showArrow={true}
    >
      <div
        className="inline-flex cursor-pointer z-[400]"
        style={{ pointerEvents: 'auto', position: 'relative' }}
        onMouseEnter={() => log('UserInfoTooltip icon mouse enter', { userId })}
        onMouseLeave={() => log('UserInfoTooltip icon mouse leave', { userId })}
        onClick={() => log('UserInfoTooltip icon clicked', { userId })}
      >
        {renderIcon()}
      </div>
    </Tooltip>
  );
};

export default UserInfoTooltip;
